import React from 'react';

export default function HelpView() {
  return (
    <div style={{ overflowY: 'auto', height: '100%', background: 'var(--bg)' }} data-testid="help-view">
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '28px 24px 80px' }}>
        <h1 style={{ color: 'var(--brand-navy)', textAlign: 'center' }}>How to use the LPA Datasheet Generator</h1>
        <p style={{ textAlign: 'center', color: 'var(--text-soft)' }}>A detailed guide to every page and feature.</p>

        <Card title="Signing in & your account">
          <ul>
            <li>Sign in with your email (or username) and password. Trailing spaces are trimmed automatically. Microsoft SSO is available once configured.</li>
            <li><b>First login:</b> if you were given a temporary password, the app asks you to set your own password (minimum 8 characters) before you can continue.</li>
            <li>After 8 wrong passwords the account is locked for 15 minutes as a security measure.</li>
          </ul>
        </Card>

        <Card title="AU / US Mode (regions)">
          <ul>
            <li>The app runs in two variants: <b>AU</b> and <b>US</b> — a badge next to the logo shows which one you're in.</li>
            <li><b>Global</b> users get an <b>AU/US Mode</b> switch top-right. Users assigned to a single region are locked to it and never see the switch.</li>
            <li>Each region has its own datasheets, families, projects and quotes. The Archive/Migrate legacy library is shared.</li>
          </ul>
        </Card>

        <Card title="Editor (datasheets)">
          <p>Pick a datasheet from the library tabs or press <b>New</b>. The preview updates live; <b>Save</b> stores it. <b>Export PDF</b> produces a pixel-accurate A4 sheet.</p>
          <ul>
            <li><b>Variant table:</b> empty columns are hidden automatically on the finished sheet & PDF.</li>
            <li><b>Badges:</b> toggle compliance badges; they wrap after 7.</li>
            <li><b>US housing datasheets</b> use the spec-submittal layout: PROJECT / TYPE / CATALOG boxes, HOUSING DATA table, LED sources, ordering matrix and ordering example — most of it is derived automatically from the product code.</li>
          </ul>
        </Card>

        <Card title="Pricing Calc — step by step">
          <ol>
            <li><b>Add a row</b> and type the item code in <b>Description</b>. Press <b>Find</b> (or Enter) to search NetSuite — if the item exists, its vendor <b>cost is pulled automatically</b> and the code is set to the exact NetSuite code. Multiple matches pop up a picker.</li>
            <li>Set the <b>supplier</b>, <b>currency</b> and <b>freight %</b> — landed cost and all price tiers calculate instantly. (EU trade discounts apply automatically per supplier.)</li>
            <li>Use the <b>Table size</b> slider to fit more rows on screen. The page scrolls when the table gets long.</li>
            <li>Tick <b>Quote</b> on any row to send it into the Quote Builder below. The item is checked against NetSuite in the background — descriptions and display names fill automatically.</li>
          </ol>
        </Card>

        <Card title="Quote Builder — step by step">
          <ol>
            <li><b>Opportunity first:</b> type the opportunity number (created by staff in NetSuite, e.g. OPAU025202). The customer autofills once it's found.</li>
            <li><b>Add items:</b> either tick rows in the pricing table, or type a code in the add box. Items found in NetSuite pull their <b>Display Name and Description automatically</b>; unknown codes are quoted as <b>Generic Budgetary Item</b> with your code as the display name.</li>
            <li>Fill <b>Line Ref</b> (e.g. CL-01) and <b>W/O Notes</b> per line if needed. Pick a price tier per line (Wholesale +5 is the default) or set a custom price.</li>
            <li><b>Save quote</b> — this also creates/updates a project named after the opportunity, attaches every matching datasheet, and flags any items that still need a datasheet (see Projects).</li>
            <li><b>Create quote in NetSuite:</b> pick the date and sales rep, then push. The quote is attached to the opportunity in NetSuite (probability 10%, Melbourne Warehouse, your name in the memo).</li>
            <li><b>Editing later:</b> reopen the quote (Quotes tab or by typing the opportunity number) and press <b>Update quote in NetSuite</b> — removed items are deleted off the NetSuite quote and all changes sync to the same quote number.</li>
          </ol>
        </Card>

        <Card title="Quotes">
          <p>All your saved quotes, one per opportunity — private to you. Open one to keep editing in Pricing Calc. A green <b>NS:</b> chip links straight to the quote in NetSuite once it's been pushed.</p>
        </Card>

        <Card title="Projects & missing datasheets">
          <p>Create a project and assign team members. Datasheets in a project are independent copies. Projects created by saving a quote are named after the opportunity number.</p>
          <ul>
            <li>An amber <b>Datasheets required</b> panel flags quoted items that had no matching datasheet. Press <b>Upload datasheet</b> on a flagged row to drop a PDF straight into that slot — the flag clears and the sheet joins the pack.</li>
            <li><b>Export PDF</b> builds the full proposal pack.</li>
          </ul>
        </Card>

        <Card title="Proposal Studio">
          <ul>
            <li>Open a project and press the studio button to arrange the pack: cover page, reorder sheets, upload extra PDFs.</li>
            <li><b>+ TYPE box</b> adds a movable red type reference to whichever page is scrolled into view (works on page 2+). New boxes on later pages copy the page-1 text automatically for consistency — still fully movable.</li>
            <li><b>+ Text box</b> adds movable text; the <b>HL</b> button on its toolbar highlights the text in yellow (shows in exports too). The highlighter tool draws yellow boxes anywhere on a page.</li>
          </ul>
        </Card>

        <Card title="SKU's (Master Admin only)">
          <ol>
            <li>Upload an Excel (.xlsx) of SKUs — the SKU column is detected automatically (headers like “SKU”, “Item”, “Code”), or the first column is used. Up to 200 SKUs at a time.</li>
            <li>Every SKU is matched against NetSuite in batches with a progress bar and time estimate — nothing times out.</li>
            <li>Per row: see the NetSuite match (display name + description), <b>Load</b> the vendor cost (or <b>Load all vendor costs</b>), enter a new cost and <b>Update price</b>, and <b>search & attach a datasheet</b> from the library.</li>
            <li><b>Save list</b> keeps the whole grid so you can come back later; reopen from the “Open a saved list” picker.</li>
            <li><i>Note:</i> pushing vendor prices requires SOAP Web Services to be enabled on the NetSuite integration — the app shows exactly what to ask your NetSuite admin if it isn't yet.</li>
          </ol>
        </Card>

        <Card title="Migrate (convert old datasheets)">
          <ul>
            <li><b>Upload</b> an old datasheet (PDF or image); it appears side-by-side with the new builder.</li>
            <li><b>Drag a box</b> over any region: <b>Send image</b> drops the crop into a field; <b>Extract text (OCR)</b> reads values into fields.</li>
            <li>Pick a <b>Family</b> and <b>Save to library</b> — stamped with your current region.</li>
          </ul>
        </Card>

        <Card title="Product Code Generator">
          <p><b>AU:</b> build LPA product codes and descriptions from dropdowns, or reverse it with <b>Fill from current datasheet</b>. <b>US:</b> uses the US coding system including housings (H-codes).</p>
        </Card>

        <Card title="AI Import">
          <p>Drop a supplier PDF into <b>AI Import</b> — the AI drafts a datasheet (specs, variants, badges, images) side-by-side with the original. Review, <b>Open in Editor</b>, fix and Save. Supplier pricing/bank details are never copied.</p>
        </Card>

        <Card title="NetSuite (Master Admin)">
          <ul>
            <li><b>Search & push:</b> find items, create new ones or merge into existing — restricted fields use approved dropdown options.</li>
            <li><b>Vendor pricing updates</b> need SOAP Web Services enabled on the integration (multi-currency limitation of the REST API).</li>
          </ul>
        </Card>

        <Card title="Admin">
          <ul>
            <li><b>Users & Roles</b> — set each person's role and region (AU only / US only / Global).</li>
            <li><b>Allowed Users</b> — create accounts: pick role + region, press <b>+ Create account</b> and a <b>temporary password</b> is generated and shown once — copy it and give it to the person. They'll set their own password at first login. AU-only/US-only users are region-locked with no switch.</li>
            <li><b>Audit Log</b> — every action, filterable by category (Log Ins, Account Creations, Datasheet Creations, Project Creations, Proposal Packages, Quote Creations, NetSuite Pushes, Misc.) with search.</li>
          </ul>
        </Card>

        <Card title="Roles">
          <p><b>Master Admin</b>: everything incl. NetSuite & SKU's. <b>Management</b>: admin & datasheets, quotes. <b>Sales & Technical</b>: datasheets, projects, migrate. <b>Guest</b>: view & download only.</p>
        </Card>
      </div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', margin: '0 0 14px' }}>
      <h2 style={{ margin: '0 0 8px', fontSize: 17, color: 'var(--brand-navy)' }}>{title}</h2>
      <div style={{ fontSize: 13.5, color: 'var(--text-soft)', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}
